import { memo, useEffect, useMemo, useRef } from "react";

const DEFAULT_COLS = 34;
const DEFAULT_ROWS = 18;
const SYMBOLS = [".", ":", "+", "-", "=", "*", "/", "#"];

function pseudoRandom(seed, index) {
   const value = Math.sin(seed * 97.31 + index * 41.73) * 10000;
   return value - Math.floor(value);
}

function getIntensity(pattern, x, y) {
   if (pattern === "corner") {
      return Math.max(0, 1 - Math.hypot(x - 0.08, y - 0.1) / 0.72);
   }

   if (pattern === "center-soft") {
      return Math.max(0, 1 - Math.hypot(x - 0.55, y - 0.5) / 0.66) * 0.42;
   }

   if (pattern === "right-radial") {
      return Math.max(0, 1 - Math.hypot(x - 0.9, y - 0.42) / 0.7) * 0.72;
   }

   if (pattern === "bottom-line") {
      return Math.max(0, 1 - Math.abs(x - y - 0.1) / 0.36) * 0.68;
   }

   return Math.max(0, 1 - Math.abs(x + y - 0.92) / 0.34);
}

function buildMatrixCells({
   cols = DEFAULT_COLS,
   rows = DEFAULT_ROWS,
   seed = 1,
   pattern = "linear",
}) {
   const cells = [];

   for (let index = 0; index < cols * rows; index += 1) {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = col / Math.max(1, cols - 1);
      const y = row / Math.max(1, rows - 1);
      const noise = pseudoRandom(seed, index);
      const finalIntensity = Math.min(1, getIntensity(pattern, x, y) + noise * 0.08);

      if (finalIntensity < 0.08) {
         continue;
      }

      cells.push({
         index,
         symbol: SYMBOLS[Math.floor(noise * SYMBOLS.length) % SYMBOLS.length],
         style: {
            gridColumn: String(col + 1),
            gridRow: String(row + 1),
            "--matrix-mix": `${Math.round((0.18 + finalIntensity * 0.82) * 100)}%`,
            "--matrix-opacity": (0.035 + finalIntensity * 0.18 + noise * 0.035).toFixed(3),
            "--matrix-scale": (0.72 + finalIntensity * 0.42).toFixed(3),
         },
      });
   }

   return cells;
}

function parseRgb(value) {
   return String(value)
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((part) => Number.isFinite(part))
      .slice(0, 3);
}

function mixRgb(a, b, mix) {
   const amount = Math.max(0, Math.min(1, mix));

   return a.map((value, index) =>
      Math.round(value * amount + (b[index] || 0) * (1 - amount)),
   );
}

function MatrixBackdrop({
   className = "",
   cols = DEFAULT_COLS,
   rows = DEFAULT_ROWS,
   seed = 1,
   pattern = "linear",
   colorA = "218, 255, 86",
   colorB = "111, 111, 255",
}) {
   const canvasRef = useRef(null);
   const isVisibleRef = useRef(false);
   const cells = useMemo(
      () => buildMatrixCells({ cols, rows, seed, pattern }),
      [cols, rows, seed, pattern],
   );
   const colors = useMemo(
      () => ({
         a: parseRgb(colorA),
         b: parseRgb(colorB),
      }),
      [colorA, colorB],
   );

   useEffect(() => {
      const canvas = canvasRef.current;
      const root = canvas?.parentElement;
      if (!canvas || !root || typeof window === "undefined") return undefined;

      const context = canvas.getContext("2d", { alpha: true });
      if (!context) return undefined;

      let raf = 0;
      let visibilityObserver = null;

      const draw = () => {
         raf = 0;
         if (!isVisibleRef.current) return;

         const rect = root.getBoundingClientRect();
         const width = Math.max(1, Math.floor(rect.width));
         const height = Math.max(1, Math.floor(rect.height));
         const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
         const viewportWidth = window.innerWidth || width;
         const fontSize = Math.max(8, Math.min(14, viewportWidth * 0.009));
         const cellWidth = width / Math.max(1, cols);
         const cellHeight = height / Math.max(1, rows);

         canvas.width = Math.floor(width * dpr);
         canvas.height = Math.floor(height * dpr);
         context.setTransform(dpr, 0, 0, dpr, 0, 0);
         context.clearRect(0, 0, width, height);
         context.textAlign = "center";
         context.textBaseline = "middle";
         context.font = `800 ${fontSize}px "SFMono-Regular", "Cascadia Mono", Consolas, monospace`;

         cells.forEach((cell) => {
            const col = cell.index % cols;
            const row = Math.floor(cell.index / cols);
            const mix = Number.parseFloat(cell.style["--matrix-mix"]) / 100;
            const opacity = Number.parseFloat(cell.style["--matrix-opacity"]);
            const scale = Number.parseFloat(cell.style["--matrix-scale"]);
            const [r, g, b] = mixRgb(colors.a, colors.b, mix);

            context.save();
            context.globalAlpha = Number.isFinite(opacity) ? opacity : 0.12;
            context.fillStyle = `rgb(${r}, ${g}, ${b})`;
            context.translate((col + 0.5) * cellWidth, (row + 0.5) * cellHeight);
            context.scale(scale, scale);
            context.fillText(cell.symbol, 0, 0);
            context.restore();
         });
      };

      const scheduleDraw = () => {
         if (!isVisibleRef.current) return;
         if (raf) return;
         raf = window.requestAnimationFrame(draw);
      };

      if ("IntersectionObserver" in window) {
         visibilityObserver = new IntersectionObserver(
            ([entry]) => {
               isVisibleRef.current = entry.isIntersecting;
               if (entry.isIntersecting) scheduleDraw();
            },
            { rootMargin: "180px 0px", threshold: 0.01 },
         );
         visibilityObserver.observe(root);
      } else {
         isVisibleRef.current = true;
         scheduleDraw();
      }

      const observer =
         "ResizeObserver" in window ? new ResizeObserver(scheduleDraw) : null;

      observer?.observe(root);
      if (!observer) window.addEventListener("resize", scheduleDraw);

      return () => {
         if (raf) window.cancelAnimationFrame(raf);
         visibilityObserver?.disconnect();
         observer?.disconnect();
         if (!observer) window.removeEventListener("resize", scheduleDraw);
      };
   }, [cells, colors, cols, rows]);

   return (
      <div
         className={`matrix-backdrop ${className}`}
         style={{
            "--matrix-cols": cols,
            "--matrix-rows": rows,
            "--matrix-color-a": colorA,
            "--matrix-color-b": colorB,
         }}
         aria-hidden="true"
      >
         <canvas ref={canvasRef} />
      </div>
   );
}

export default memo(MatrixBackdrop);
